import { css } from 'lit';
export const analyticsStyles = css`
      :host {
        display: block;
        font-family: var(--font-body);
        color: var(--color-text-primary);
      }

      .dashboard-layout {
        display: flex;
        flex-direction: column;
        gap: var(--space-8);
        animation: fadeIn var(--duration-slow) var(--ease-out) both;
      }

      /* ─── Time Range Selector Row ─── */
      .controls-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: var(--space-4);
        flex-wrap: wrap;
      }

      .controls-left {
        display: flex;
        align-items: center;
        gap: var(--space-3);
      }

      .time-pills {
        display: flex;
        background: var(--color-bg-secondary);
        border: 1px solid var(--color-border-primary);
        border-radius: var(--radius-md);
        padding: 3px;
        gap: 2px;
      }

      .time-pill {
        padding: var(--space-1) var(--space-4);
        border: none;
        border-radius: var(--radius-sm);
        background: transparent;
        color: var(--color-text-secondary);
        font-family: var(--font-body);
        font-size: var(--text-sm);
        font-weight: var(--font-medium);
        cursor: pointer;
        transition: all var(--transition-fast);
        white-space: nowrap;
      }

      .time-pill:hover {
        background: var(--color-bg-tertiary);
        color: var(--color-text-primary);
      }

      .time-pill.active {
        background: var(--color-accent);
        color: var(--color-text-inverse);
        box-shadow: var(--shadow-sm);
      }

      .agent-select {
        padding: var(--space-2) var(--space-3);
        padding-right: var(--space-8);
        background: var(--color-bg-secondary);
        border: 1px solid var(--color-border-primary);
        border-radius: var(--radius-md);
        color: var(--color-text-primary);
        font-family: var(--font-body);
        font-size: var(--text-sm);
        cursor: pointer;
        transition: all var(--transition-fast);
        appearance: none;
        background-image: url("data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L5 5L9 1' stroke='%23A8A29E' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
        background-repeat: no-repeat;
        background-position: right 10px center;
      }

      .agent-select:focus {
        outline: none;
        border-color: var(--color-primary);
        box-shadow: var(--shadow-glow-primary);
      }

      .refresh-btn {
        width: 36px;
        height: 36px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        background: var(--color-bg-secondary);
        border: 1px solid var(--color-border-primary);
        border-radius: var(--radius-md);
        color: var(--color-text-secondary);
        cursor: pointer;
        transition: all var(--transition-fast);
        padding: 0;
        font-size: var(--text-sm);
      }

      .refresh-btn:hover {
        background: var(--color-bg-tertiary);
        color: var(--color-primary);
        border-color: var(--color-border-secondary);
      }

      /* ─── 6 KPI Cards ─── */
      .kpi-grid {
        display: grid;
        grid-template-columns: repeat(6, 1fr);
        gap: var(--space-3);
      }

      .kpi-card {
        background: rgba(255, 255, 255, 0.18);
        backdrop-filter: blur(8px) saturate(1.2);
        -webkit-backdrop-filter: blur(8px) saturate(1.2);
        border: 1px solid rgba(255, 255, 255, 0.35);
        border-radius: var(--radius-xl);
        padding: var(--space-6);
        display: flex;
        flex-direction: column;
        gap: var(--space-2);
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.5), inset 0 -1px 0 rgba(255, 255, 255, 0.08);
        transition: all var(--transition-base);
        position: relative;
        overflow: hidden;
      }

      .kpi-card:hover {
        box-shadow: var(--shadow-hover-lift);
        border-color: var(--color-border-secondary);
        transform: scale(1.01);
      }

      .kpi-card::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 3px;
        background: var(--kpi-accent, var(--color-primary));
      }

      .kpi-top {
        display: flex;
        align-items: center;
        justify-content: space-between;
      }

      .kpi-icon {
        width: 48px;
        height: 48px;
        border-radius: var(--radius-lg);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: var(--text-base);
        flex-shrink: 0;
        background: linear-gradient(135deg, var(--kpi-bg, var(--color-primary-light)), var(--kpi-accent, var(--color-primary)));
        color: #fff;
      }

      .kpi-trend {
        display: inline-flex;
        align-items: center;
        gap: 2px;
        font-family: var(--font-mono);
        font-size: var(--text-xs);
        font-weight: var(--font-semibold);
        padding: 1px var(--space-2);
        border-radius: var(--radius-full);
      }

      .kpi-trend.up {
        background: var(--color-success-bg);
        color: var(--color-success-text);
      }

      .kpi-trend.down {
        background: var(--color-error-bg);
        color: var(--color-error-text);
      }

      .kpi-value {
        font-family: var(--font-display);
        font-size: var(--text-4xl);
        font-weight: var(--font-black);
        color: var(--color-text-primary);
        line-height: var(--leading-tight);
        letter-spacing: var(--tracking-tighter);
      }

      .kpi-label {
        font-size: var(--text-xs);
        color: var(--color-text-tertiary);
        font-weight: var(--font-medium);
        text-transform: uppercase;
        letter-spacing: var(--tracking-wider);
      }

      /* CSS-only sparkline: 7 thin bars */
      .kpi-sparkline {
        display: flex;
        align-items: flex-end;
        gap: 2px;
        height: 24px;
        margin-top: var(--space-1);
      }

      .spark-bar {
        flex: 1;
        border-radius: 1px;
        background: var(--kpi-accent, var(--color-primary));
        opacity: 0.35;
        transition: opacity var(--transition-fast);
      }

      .kpi-card:hover .spark-bar {
        opacity: 0.55;
      }

      /* ─── Call Volume Trend Chart ─── */
      .section-card {
        background: rgba(255, 255, 255, 0.18);
        backdrop-filter: blur(8px) saturate(1.2);
        -webkit-backdrop-filter: blur(8px) saturate(1.2);
        border: 1px solid rgba(255, 255, 255, 0.35);
        border-radius: var(--radius-xl);
        padding: var(--space-6);
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.5), inset 0 -1px 0 rgba(255, 255, 255, 0.08);
        position: relative;
        overflow: hidden;
      }

      .section-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: var(--space-4);
      }

      .section-title-group {
        display: flex;
        flex-direction: column;
        gap: 2px;
      }

      .section-title {
        font-family: var(--font-display);
        font-size: var(--text-lg);
        font-weight: var(--font-bold);
        color: var(--color-text-primary);
        letter-spacing: var(--tracking-tight);
      }

      .section-subtitle {
        font-size: var(--text-xs);
        color: var(--color-text-tertiary);
      }

      /* Bar chart */
      .bar-chart {
        display: flex;
        flex-direction: column;
        gap: var(--space-2);
      }

      .bar-chart-area {
        display: flex;
        align-items: flex-end;
        gap: 3px;
        height: 200px;
        padding: 0 var(--space-1);
        position: relative;
      }

      /* Y-axis labels */
      .bar-chart-y-axis {
        position: absolute;
        left: -4px;
        top: 0;
        bottom: 0;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        pointer-events: none;
      }

      .y-label {
        font-family: var(--font-mono);
        font-size: 9px;
        color: var(--color-text-disabled);
        text-align: right;
        width: 28px;
        transform: translateY(4px);
      }

      .bar-column {
        flex: 1;
        display: flex;
        flex-direction: column;
        align-items: center;
        position: relative;
        cursor: pointer;
      }

      .bar-fill {
        width: 100%;
        max-width: 28px;
        border-radius: 3px 3px 0 0;
        background: linear-gradient(180deg, var(--color-primary), rgba(45, 143, 111, 0.6));
        transition: all var(--transition-fast);
        position: relative;
      }

      .bar-column:hover .bar-fill {
        background: linear-gradient(180deg, var(--color-primary-hover), var(--color-primary));
        box-shadow: 0 -2px 8px rgba(45, 143, 111, 0.25);
      }

      .bar-tooltip {
        position: absolute;
        bottom: calc(100% + 6px);
        left: 50%;
        transform: translateX(-50%);
        background: var(--color-text-primary);
        color: var(--color-text-inverse);
        padding: 3px 8px;
        border-radius: var(--radius-sm);
        font-family: var(--font-mono);
        font-size: 10px;
        white-space: nowrap;
        pointer-events: none;
        z-index: var(--z-tooltip);
        opacity: 0;
        transition: opacity var(--transition-fast);
      }

      .bar-column:hover .bar-tooltip {
        opacity: 1;
      }

      .bar-chart-x-axis {
        display: flex;
        gap: 3px;
        padding: 0 var(--space-1);
      }

      .x-label {
        flex: 1;
        text-align: center;
        font-family: var(--font-mono);
        font-size: 9px;
        color: var(--color-text-disabled);
      }

      .x-label.highlight {
        color: var(--color-text-tertiary);
        font-weight: var(--font-semibold);
      }

      /* ─── Two-column Layout ─── */
      .two-col {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: var(--space-8);
      }

      /* ─── Conversion Funnel ─── */
      .funnel-stages {
        display: flex;
        flex-direction: column;
        gap: var(--space-3);
      }

      .funnel-stage {
        display: flex;
        align-items: center;
        gap: var(--space-3);
      }

      .funnel-bar-wrap {
        flex: 1;
        position: relative;
      }

      .funnel-bar {
        height: 44px;
        border-radius: var(--radius-md);
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0 var(--space-3);
        transition: all var(--transition-base);
        background: linear-gradient(90deg, var(--color-primary), rgba(45, 143, 111, 0.65));
        position: relative;
        overflow: hidden;
      }

      .funnel-bar::after {
        content: '';
        position: absolute;
        inset: 0;
        background: linear-gradient(90deg, transparent 70%, rgba(255,255,255,0.15));
      }

      .funnel-bar:hover {
        transform: translateX(3px);
        box-shadow: var(--shadow-md);
      }

      .funnel-stage-label {
        font-size: var(--text-sm);
        font-weight: var(--font-medium);
        color: var(--color-text-inverse);
        z-index: 1;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .funnel-stage-count {
        font-family: var(--font-mono);
        font-size: var(--text-sm);
        font-weight: var(--font-bold);
        color: var(--color-text-inverse);
        z-index: 1;
      }

      .funnel-pct {
        min-width: 50px;
        text-align: right;
        font-family: var(--font-mono);
        font-size: var(--text-sm);
        font-weight: var(--font-bold);
        color: var(--color-text-primary);
      }

      /* Connector arrows */
      .funnel-connector {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 12px;
        color: var(--color-text-disabled);
        font-size: 10px;
      }

      /* ─── Peak Hours Heatmap ─── */
      .heatmap-container {
        overflow-x: auto;
      }

      .heatmap-grid {
        display: grid;
        grid-template-columns: 40px repeat(24, 1fr);
        gap: 2px;
        min-width: 500px;
      }

      .heatmap-row-label {
        font-size: var(--text-xs);
        font-weight: var(--font-semibold);
        color: var(--color-text-tertiary);
        display: flex;
        align-items: center;
        padding-right: var(--space-2);
      }

      .heatmap-col-header {
        font-family: var(--font-mono);
        font-size: 8px;
        color: var(--color-text-disabled);
        text-align: center;
        padding-bottom: 2px;
      }

      .heatmap-corner {
        /* empty top-left corner */
      }

      .heat-cell {
        aspect-ratio: 1;
        border-radius: 3px;
        cursor: pointer;
        transition: all var(--transition-fast);
        position: relative;
      }

      .heat-cell:hover {
        transform: scale(1.15);
        z-index: 5;
        box-shadow: var(--shadow-md);
      }

      /* Intensity levels using primary color */
      .heat-cell.i0 { background: var(--color-bg-tertiary); }
      .heat-cell.i1 { background: rgba(45, 143, 111, 0.12); }
      .heat-cell.i2 { background: rgba(45, 143, 111, 0.28); }
      .heat-cell.i3 { background: rgba(45, 143, 111, 0.45); }
      .heat-cell.i4 { background: rgba(45, 143, 111, 0.65); }
      .heat-cell.i5 { background: rgba(45, 143, 111, 0.85); }

      .heat-cell-tooltip {
        position: absolute;
        bottom: calc(100% + 4px);
        left: 50%;
        transform: translateX(-50%);
        background: var(--color-text-primary);
        color: var(--color-text-inverse);
        padding: 2px 6px;
        border-radius: var(--radius-sm);
        font-family: var(--font-mono);
        font-size: 9px;
        white-space: nowrap;
        pointer-events: none;
        z-index: var(--z-tooltip);
        opacity: 0;
        transition: opacity var(--transition-fast);
      }

      .heat-cell:hover .heat-cell-tooltip {
        opacity: 1;
      }

      .heatmap-legend {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        margin-top: var(--space-3);
        justify-content: flex-end;
      }

      .legend-label {
        font-size: var(--text-xs);
        color: var(--color-text-tertiary);
      }

      .legend-scale {
        display: flex;
        gap: 2px;
      }

      .legend-block {
        width: 14px;
        height: 14px;
        border-radius: 2px;
      }

      /* ─── Performance Breakdown ─── */
      .perf-row {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: var(--space-3);
      }

      .perf-card {
        background: rgba(255, 255, 255, 0.18);
        backdrop-filter: blur(8px) saturate(1.2);
        -webkit-backdrop-filter: blur(8px) saturate(1.2);
        border: 1px solid rgba(255, 255, 255, 0.35);
        border-radius: var(--radius-xl);
        padding: var(--space-6);
        display: flex;
        flex-direction: column;
        gap: var(--space-2);
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.5), inset 0 -1px 0 rgba(255, 255, 255, 0.08);
        position: relative;
        overflow: hidden;
      }

      .perf-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
      }

      .perf-label {
        font-size: var(--text-xs);
        color: var(--color-text-tertiary);
        font-weight: var(--font-medium);
        text-transform: uppercase;
        letter-spacing: var(--tracking-wider);
      }

      .perf-value {
        font-family: var(--font-display);
        font-size: var(--text-xl);
        font-weight: var(--font-bold);
        color: var(--color-text-primary);
        letter-spacing: var(--tracking-tight);
      }

      .perf-bar-track {
        height: 8px;
        background: var(--color-bg-inset);
        border-radius: var(--radius-full);
        overflow: hidden;
      }

      .perf-bar-fill {
        height: 100%;
        border-radius: var(--radius-full);
        transition: width var(--duration-slower) var(--ease-out);
      }

      /* ─── Responsive ─── */
      @media (max-width: 1200px) {
        .kpi-grid {
          grid-template-columns: repeat(3, 1fr);
        }
      }

      @media (max-width: 900px) {
        .kpi-grid {
          grid-template-columns: repeat(2, 1fr);
        }
        .two-col {
          grid-template-columns: 1fr;
        }
        .perf-row {
          grid-template-columns: repeat(2, 1fr);
        }
      }

      @media (max-width: 640px) {
        .kpi-grid {
          grid-template-columns: 1fr;
        }
        .perf-row {
          grid-template-columns: 1fr;
        }
        .controls-row {
          flex-direction: column;
          align-items: stretch;
        }
      }
    `;
